var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { showToast } from 'stores/ToastStore';
import SessionStore from 'stores/SessionStore';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import Spinner from 'components/ui/Spinner';
import classNames from 'classnames';
import EditableSection from 'components/v2/EditableSection';
import CustomDropdown from 'components/v2/dropdowns/CustomDropdown';
import FormGroup from 'components/v2/FormGroup';
import { getUserBillReadyEvents, upsertUserBillReadyEvents, } from 'utils/UserServiceAPIUtils';
import { DELIVERY_LOADED_OR_EMPTY, EMPTY_CONTAINER_RETURNED, FULL_CONTAINER_DELIVERED, FULL_CONTAINER_PICKED_UP_LOADED, PICKUP_LOADED_OR_EMPTY, } from 'constants/billing-status';
const SHIPMENT_MOVEMENT_EVENT_TYPES = [
    'import_drayage_event',
    'import_rail_event',
    'domestic_delivery_drayage_event',
    'domestic_delivery_rail_event',
    'export_drayage_event',
    'export_rail_event',
    'domestic_pickup_drayage_event',
    'domestic_pickup_rail_event',
    'reposition_drayage_event',
    'reposition_rail_event',
];
const IMPORT_OR_DOMESTIC_DELIVERY_SLUGS = [
    FULL_CONTAINER_DELIVERED,
    EMPTY_CONTAINER_RETURNED,
];
const EXPORT_OR_DOMESTIC_PICKUP_SLUGS = [
    FULL_CONTAINER_PICKED_UP_LOADED,
    FULL_CONTAINER_DELIVERED,
];
const REPOSITION_SLUGS = [PICKUP_LOADED_OR_EMPTY, DELIVERY_LOADED_OR_EMPTY];
function BillReadyEventSettingsSummary({ billReadyEventData }) {
    const { import_drayage_event, import_rail_event, export_drayage_event, export_rail_event, domestic_delivery_drayage_event, domestic_delivery_rail_event, domestic_pickup_drayage_event, domestic_pickup_rail_event, reposition_drayage_event, reposition_rail_event, } = billReadyEventData || {};
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    function renderSpinner() {
        return (React.createElement("div", { className: "grid grid-cols-2 gap-4" },
            React.createElement(Spinner, { showMessage: true, message: t('LOADING') })));
    }
    useEffect(() => {
        if (Object.keys(billReadyEventData).length) {
            setIsLoading(false);
        }
        else {
            setIsLoading(true);
        }
    }, [billReadyEventData]);
    return isLoading ? (renderSpinner()) : (React.createElement("div", { className: "grid grid-cols-2 gap-4" },
        React.createElement(DescriptionListItem, { label: t('IMPORT_DRAYAGE'), className: "" }, t(import_drayage_event === null || import_drayage_event === void 0 ? void 0 : import_drayage_event.toUpperCase())),
        React.createElement(DescriptionListItem, { label: t('IMPORT_RAIL'), className: "" }, t(import_rail_event === null || import_rail_event === void 0 ? void 0 : import_rail_event.toUpperCase())),
        React.createElement(DescriptionListItem, { label: t('DOMESTIC_DELIVERY_DRAYAGE'), className: "" }, t(domestic_delivery_drayage_event === null || domestic_delivery_drayage_event === void 0 ? void 0 : domestic_delivery_drayage_event.toUpperCase())),
        React.createElement(DescriptionListItem, { label: t('DOMESTIC_DELIVERY_RAIL'), className: "" }, t(domestic_delivery_rail_event === null || domestic_delivery_rail_event === void 0 ? void 0 : domestic_delivery_rail_event.toUpperCase())),
        React.createElement(DescriptionListItem, { label: t('EXPORT_DRAYAGE'), className: "" }, t(export_drayage_event === null || export_drayage_event === void 0 ? void 0 : export_drayage_event.toUpperCase())),
        React.createElement(DescriptionListItem, { label: t('EXPORT_RAIL'), className: "" }, t(export_rail_event === null || export_rail_event === void 0 ? void 0 : export_rail_event.toUpperCase())),
        React.createElement(DescriptionListItem, { label: t('DOMESTIC_PICKUP_DRAYAGE'), className: "" }, t(domestic_pickup_drayage_event === null || domestic_pickup_drayage_event === void 0 ? void 0 : domestic_pickup_drayage_event.toUpperCase())),
        React.createElement(DescriptionListItem, { label: t('DOMESTIC_PICKUP_RAIL'), className: "" }, t(domestic_pickup_rail_event === null || domestic_pickup_rail_event === void 0 ? void 0 : domestic_pickup_rail_event.toUpperCase())),
        React.createElement(DescriptionListItem, { label: t('REPOSITION_DELIVERY_DRAYAGE'), className: "" }, t(reposition_drayage_event === null || reposition_drayage_event === void 0 ? void 0 : reposition_drayage_event.toUpperCase())),
        React.createElement(DescriptionListItem, { label: t('REPOSITION_DELIVERY_RAIL'), className: "" }, t(reposition_rail_event === null || reposition_rail_event === void 0 ? void 0 : reposition_rail_event.toUpperCase()))));
}
function BillReadyEventSettingsForm({ billReadyEventData, userId, saving, onSaveSuccess, onSaveFail, }) {
    const { t } = useTranslation();
    const [formValues, setFormValues] = useState({});
    const slugsToOptions = (slugs) => slugs.map((slug) => ({ name: t(slug), value: slug }));
    function handleSettingFormValues() {
        const initialData = {};
        SHIPMENT_MOVEMENT_EVENT_TYPES.forEach((shipmentMovementType) => {
            const subData = {
                name: t(billReadyEventData[shipmentMovementType]),
                value: billReadyEventData[shipmentMovementType],
            };
            initialData[shipmentMovementType] = subData;
        });
        setFormValues(initialData);
    }
    useEffect(() => {
        handleSettingFormValues();
    }, []);
    useEffect(() => {
        if (saving) {
            saveForm();
        }
    }, [saving]);
    async function saveForm() {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
        try {
            const newUserBillReadyEventsSetting = {
                import_drayage_event: (_a = formValues === null || formValues === void 0 ? void 0 : formValues.import_drayage_event) === null || _a === void 0 ? void 0 : _a.value,
                import_rail_event: (_b = formValues === null || formValues === void 0 ? void 0 : formValues.import_rail_event) === null || _b === void 0 ? void 0 : _b.value,
                domestic_delivery_drayage_event: (_c = formValues === null || formValues === void 0 ? void 0 : formValues.domestic_delivery_drayage_event) === null || _c === void 0 ? void 0 : _c.value,
                domestic_delivery_rail_event: (_d = formValues === null || formValues === void 0 ? void 0 : formValues.domestic_delivery_rail_event) === null || _d === void 0 ? void 0 : _d.value,
                export_drayage_event: (_e = formValues === null || formValues === void 0 ? void 0 : formValues.export_drayage_event) === null || _e === void 0 ? void 0 : _e.value,
                export_rail_event: (_f = formValues === null || formValues === void 0 ? void 0 : formValues.export_rail_event) === null || _f === void 0 ? void 0 : _f.value,
                domestic_pickup_drayage_event: (_g = formValues === null || formValues === void 0 ? void 0 : formValues.domestic_pickup_drayage_event) === null || _g === void 0 ? void 0 : _g.value,
                domestic_pickup_rail_event: (_h = formValues === null || formValues === void 0 ? void 0 : formValues.domestic_pickup_rail_event) === null || _h === void 0 ? void 0 : _h.value,
                reposition_drayage_event: (_j = formValues === null || formValues === void 0 ? void 0 : formValues.reposition_drayage_event) === null || _j === void 0 ? void 0 : _j.value,
                reposition_rail_event: (_k = formValues === null || formValues === void 0 ? void 0 : formValues.reposition_rail_event) === null || _k === void 0 ? void 0 : _k.value,
            };
            const result = await upsertUserBillReadyEvents(userId, newUserBillReadyEventsSetting);
            const success = (_m = (_l = result === null || result === void 0 ? void 0 : result.data) === null || _l === void 0 ? void 0 : _l.upsertBillReadyEventsSetting) === null || _m === void 0 ? void 0 : _m.success;
            if (!success)
                throw new Error('Setting was not successfully updated');
            showToast(t('EDIT_BILL_READY_EVENTS_SETTING_SUCCESS'));
            onSaveSuccess();
        }
        catch (err) {
            console.error('Error during upsertUserBillReadyEvents: ', err.message);
            showToast(t('EDIT_BILL_READY_EVENTS_SETTING_FAILURE'), null, 'error');
            onSaveFail();
        }
    }
    function handleChange(field) {
        return function (value) {
            setFormValues((data) => (Object.assign(Object.assign({}, data), { [field]: value })));
        };
    }
    return (React.createElement("div", { className: "grid grid-cols-2 gap-4" },
        React.createElement(FormGroup, { label: t('IMPORT_DRAYAGE'), className: "" },
            React.createElement(CustomDropdown, { label: "", options: slugsToOptions(IMPORT_OR_DOMESTIC_DELIVERY_SLUGS), value: formValues.import_drayage_event, onChange: handleChange('import_drayage_event'), nameKey: "name", valueKey: "value" })),
        React.createElement(FormGroup, { label: t('IMPORT_RAIL'), className: "" },
            React.createElement(CustomDropdown, { label: "", options: slugsToOptions(IMPORT_OR_DOMESTIC_DELIVERY_SLUGS), value: formValues.import_rail_event, onChange: handleChange('import_rail_event'), nameKey: "name", valueKey: "value" })),
        React.createElement(FormGroup, { label: t('DOMESTIC_DELIVERY_DRAYAGE'), className: "" },
            React.createElement(CustomDropdown, { label: "", options: slugsToOptions(IMPORT_OR_DOMESTIC_DELIVERY_SLUGS), value: formValues.domestic_delivery_drayage_event, onChange: handleChange('domestic_delivery_drayage_event'), nameKey: "name", valueKey: "value" })),
        React.createElement(FormGroup, { label: t('DOMESTIC_DELIVERY_RAIL'), className: "" },
            React.createElement(CustomDropdown, { label: "", options: slugsToOptions(IMPORT_OR_DOMESTIC_DELIVERY_SLUGS), value: formValues.domestic_delivery_rail_event, onChange: handleChange('domestic_delivery_rail_event'), nameKey: "name", valueKey: "value" })),
        React.createElement(FormGroup, { label: t('EXPORT_DRAYAGE'), className: "" },
            React.createElement(CustomDropdown, { label: "", options: slugsToOptions(EXPORT_OR_DOMESTIC_PICKUP_SLUGS), value: formValues.export_drayage_event, onChange: handleChange('export_drayage_event'), nameKey: "name", valueKey: "value" })),
        React.createElement(FormGroup, { label: t('EXPORT_RAIL'), className: "" },
            React.createElement(CustomDropdown, { label: "", options: slugsToOptions(EXPORT_OR_DOMESTIC_PICKUP_SLUGS), value: formValues.export_rail_event, onChange: handleChange('export_rail_event'), nameKey: "name", valueKey: "value" })),
        React.createElement(FormGroup, { label: t('DOMESTIC_PICKUP_DRAYAGE'), className: "" },
            React.createElement(CustomDropdown, { label: "", options: slugsToOptions(EXPORT_OR_DOMESTIC_PICKUP_SLUGS), value: formValues.domestic_pickup_drayage_event, onChange: handleChange('domestic_pickup_drayage_event'), nameKey: "name", valueKey: "value" })),
        React.createElement(FormGroup, { label: t('DOMESTIC_PICKUP_RAIL'), className: "" },
            React.createElement(CustomDropdown, { label: "", options: slugsToOptions(EXPORT_OR_DOMESTIC_PICKUP_SLUGS), value: formValues.domestic_pickup_rail_event, onChange: handleChange('domestic_pickup_rail_event'), nameKey: "name", valueKey: "value" })),
        React.createElement(FormGroup, { label: t('REPOSITION_DELIVERY_DRAYAGE'), className: "" },
            React.createElement(CustomDropdown, { label: "", options: slugsToOptions(REPOSITION_SLUGS), value: formValues.reposition_drayage_event, onChange: handleChange('reposition_drayage_event'), nameKey: "name", valueKey: "value" })),
        React.createElement(FormGroup, { label: t('REPOSITION_DELIVERY_RAIL'), className: "" },
            React.createElement(CustomDropdown, { label: "", options: slugsToOptions(REPOSITION_SLUGS), value: formValues.reposition_rail_event, onChange: handleChange('reposition_rail_event'), nameKey: "name", valueKey: "value" }))));
}
export default function BillReadyEventSettings({ userId, className }) {
    const { t } = useTranslation();
    const [userBillReadyEventsSetting, setUserBillReadyEventsSetting] = useState({});
    const [canEdit, setCanEdit] = useState(false);
    const isShipperOnboarder = SessionStore.hasShipperOnboardingRole();
    const fetchUserBillReadyEventsSetting = async () => {
        var _a;
        try {
            const result = await getUserBillReadyEvents(userId);
            const _b = ((_a = result === null || result === void 0 ? void 0 : result.data) === null || _a === void 0 ? void 0 : _a.getBillReadyEventsSetting) || {}, { user_id } = _b, billReadyEvents = __rest(_b, ["user_id"]);
            if (!billReadyEvents)
                throw new Error('Missing Bill Ready Events data');
            setUserBillReadyEventsSetting(billReadyEvents);
            setCanEdit(true);
        }
        catch (e) {
            console.error('Error fetching getUserBillReadyEvents', e.message);
        }
    };
    useEffect(() => {
        fetchUserBillReadyEventsSetting();
    }, []);
    const handleAfterSave = (onSaveSuccess) => {
        return () => {
            fetchUserBillReadyEventsSetting();
            onSaveSuccess();
        };
    };
    return (React.createElement(EditableSection, { title: t('DRAYAGE_BILL_READY_EVENTS'), className: classNames(className), canEdit: canEdit && isShipperOnboarder }, (editing, saving, onSaveSuccess, onSaveFail) => (React.createElement(React.Fragment, null,
        !editing && (React.createElement(BillReadyEventSettingsSummary, { billReadyEventData: userBillReadyEventsSetting })),
        editing && (React.createElement(BillReadyEventSettingsForm, { saving: saving, billReadyEventData: userBillReadyEventsSetting, userId: userId, onSaveSuccess: handleAfterSave(onSaveSuccess), onSaveFail: onSaveFail }))))));
}
